import { useEffect, useState } from "react"
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import ProtectedRoute from "./components/ProtectedRoute"
import PublicRoute from "./components/PublicRoute"
import OtpVerifyRoute from "./components/OtpVerifyRoute"
import {
  protectedRoutes,
  publicRoutes,
  otpRoutes,
  getLandingPath,
} from "./routes"
import Navbar from "./components/Navbar"
import { useIdleTimer } from "react-idle-timer"
import { AUTH_USER_KEY, useAuthUser, useLogout } from "./hooks/useAuth"
import { isFullScope } from "./constants"
import { SESSION_EXPIRED_EVENT } from "./config/axios"
import routePaths from "./constants/path"
import LogoHeader from "./components/LogoHeader"
import FullPageLoader from "./components/FullPageLoader"
import SessionExpiredModal from "./components/SessionExpiredModal"
import ErrorBoundary from "./components/ErrorBoundary"
import { Toaster } from "sonner"

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Routes like educational videos are fully public and never need the
  // auth/me check, so don't fetch it there (only /login needs it, to
  // auto-redirect an already-logged-in visitor to their dashboard).
  const isPublicNonAuthRoute = publicRoutes.some(
    (route) =>
      route.path !== routePaths.login &&
      matchPath(route.path, location.pathname)
  )
  const { data, isLoading } = useAuthUser({ enabled: !isPublicNonAuthRoute })
  const { mutate: logout } = useLogout()
  const [sessionExpired, setSessionExpired] = useState(false)

  const isAuthenticated = isFullScope(data?.scope)

  useEffect(() => {
    const handleSessionExpired = () => {
      setSessionExpired(true)
      queryClient.removeQueries({ queryKey: AUTH_USER_KEY })
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
  }, [queryClient])

  const handleSessionExpiredAck = () => {
    setSessionExpired(false)
    navigate(routePaths.login, { replace: true })
  }

  const handleIdleLogout = () => {
    logout(undefined, {
      onSettled: () => {
        navigate(routePaths.login, { replace: true })
      },
    })
  }

  useIdleTimer({
    disabled: !isAuthenticated,
    timeout: 1000 * 60 * 30, // 30 minutes
    onIdle: handleIdleLogout,
    debounce: 1000,
    crossTab: true,
  })

  return (
    <>
      <Routes>
        {protectedRoutes?.map((route) => (
          <Route
            key={route.path}
            element={
              <>
                <Navbar />
                <div className="max-w-9xl mx-auto px-4 mt-6 pt-14">
                  <ErrorBoundary>
                    <ProtectedRoute
                      component={route.element}
                      whoCanAccess={route.canAccess}
                    />
                  </ErrorBoundary>
                </div>
              </>
            }
            path={route.path}
          />
        ))}
        {otpRoutes?.map((route) => (
          <Route
            key={route.path}
            element={
              <>
                <LogoHeader />
                <div className="mx-auto mt-10 pt-14">
                  <ErrorBoundary>
                    <OtpVerifyRoute
                      component={route.element}
                      requirePhase={route.requirePhase}
                    />
                  </ErrorBoundary>
                </div>
              </>
            }
            path={route.path}
          />
        ))}
        {publicRoutes?.map((route) => (
          <Route
            key={route.path}
            element={
              <>
                <LogoHeader />
                <div className="mx-auto mt-10 pt-14">
                  <ErrorBoundary>
                    {route.path === routePaths.login ? (
                      <PublicRoute component={route.element} />
                    ) : (
                      route.element
                    )}
                  </ErrorBoundary>
                </div>
              </>
            }
            path={route.path}
          />
        ))}
        <Route
          element={
            isLoading ? (
              <FullPageLoader />
            ) : isAuthenticated ? (
              <Navigate replace to={getLandingPath(data?.data?.role)} />
            ) : (
              <Navigate replace to={routePaths.login} />
            )
          }
          path="*"
        />
      </Routes>
      <SessionExpiredModal
        open={sessionExpired}
        onGoToLogin={handleSessionExpiredAck}
      />
      <Toaster closeButton richColors expand={true} position="top-right" />
    </>
  )
}

export default App
